/**
 * Driver offer accept/decline screen. Reached via push notification
 * (tag=offer-{id}) or by an in-app "You have a new offer!" banner.
 *
 * UX rules (locked in with Ivan on 2026-02-14):
 *  - 30-second countdown, visualised as a shrinking bar + numeric.
 *  - Two prominent buttons: Accept (gold), Decline (subtle red).
 *  - On timeout: show "This offer was reassigned to another driver."
 *  - On accept: bounce to /driver/dashboard with a success toast.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, X, Clock, MapPin, Navigation, User, Phone } from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

export default function DriverOffer() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(30);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await driverApi.get(`/driver/offers/${offerId}`);
        if (!alive) return;
        setOffer(data.offer);
        setBooking(data.booking);
        if (data.offer?.expires_at) {
          const s = Math.max(0,
            Math.floor((new Date(data.offer.expires_at).getTime() - Date.now()) / 1000));
          setRemaining(s);
        }
      } catch (e) {
        setError(e?.response?.status === 403
          ? "Ova ponuda pripada drugom vozaču."
          : "Ponuda nije pronađena.");
      }
    })();
    return () => { alive = false; };
  }, [offerId]);

  // Countdown tick
  useEffect(() => {
    if (!offer || offer.status !== "pending") return undefined;
    if (remaining <= 0) return undefined;
    const t = setTimeout(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [offer, remaining]);

  // Poll status every 3s after the countdown ends so we surface
  // 'timeout' or 'superseded' quickly.
  useEffect(() => {
    if (!offer || offer.status !== "pending" || remaining > 0) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await driverApi.get(`/driver/offers/${offerId}`);
        if (data.offer && data.offer.status !== "pending") {
          setOffer(data.offer);
          clearInterval(pollRef.current);
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [offer, remaining, offerId]);

  const accept = async () => {
    setBusy(true);
    try {
      await driverApi.post(`/driver/offers/${offerId}/accept`);
      toast.success("Vožnja prihvaćena — vidimo se na polazištu.");
      navigate("/driver/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prihvaćanje nije uspjelo — možda je vrijeme isteklo?");
      setBusy(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Odbiti ovu vožnju?")) return;
    setBusy(true);
    try {
      await driverApi.post(`/driver/offers/${offerId}/decline`, { reason: "" });
      toast.success("Odbijeno.");
      navigate("/driver/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odbijanje nije uspjelo.");
      setBusy(false);
    }
  };

  const progress = useMemo(() => {
    if (!offer) return 100;
    return Math.max(0, Math.min(100, (remaining / 30) * 100));
  }, [offer, remaining]);

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8">
        <p className="text-lg text-red-400">{error}</p>
        <button onClick={() => navigate("/driver/dashboard")}
                className="mt-6 h-10 px-5 rounded-full border border-white/20 text-xs uppercase tracking-widest">
          Natrag na početnu
        </button>
      </div>
    );
  }
  if (!offer) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Učitavanje…</div>;
  }

  const isPending = offer.status === "pending" && remaining > 0;
  const isExpired = offer.status !== "pending" || remaining <= 0;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="offer-screen">
      {/* Countdown ribbon */}
      <div className="h-1 bg-zinc-900">
        <div className="h-full bg-emerald-500 transition-all"
             style={{ width: `${progress}%` }} />
      </div>

      <div className="max-w-md mx-auto p-5 pt-6">
        {isPending ? (
          <div className="flex items-center justify-between mb-6">
            <span className="text-[10px] uppercase tracking-widest text-emerald-400">
              Nova ponuda
            </span>
            <span className="font-display text-4xl tabular-nums text-white"
                  data-testid="offer-countdown">
              {remaining}s
            </span>
          </div>
        ) : (
          <div className="mb-6 rounded-xl border border-orange-500/40 bg-orange-500/[0.06] p-3 flex items-center gap-2"
               data-testid="offer-expired-banner">
            <Clock className="w-4 h-4 text-orange-300" />
            <p className="text-xs text-orange-200">
              {offer.status === "accepted"     ? "Već prihvaćeno."
                : offer.status === "declined"  ? "Odbili ste ovu ponudu."
                : offer.status === "superseded" ? "Dodijeljeno drugom vozaču."
                : offer.status === "timeout"   ? "Vrijeme isteklo — proslijeđeno sljedećem vozaču."
                : offer.status === "cancelled" ? "Administrator je otkazao ponudu."
                : "Isteklo."}
            </p>
          </div>
        )}

        {/* Route card */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
            <div className="flex items-start gap-3">
              <div className="mt-1">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <div className="w-px h-8 bg-white/15 mx-auto" />
                <div className="w-2 h-2 rounded-full bg-[#C5A059]" />
              </div>
              <div className="flex-1 min-w-0 space-y-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Polazište</p>
                  <p className="text-sm text-white truncate" data-testid="offer-pickup">
                    {booking?.pickup_location || "-"}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5">
                    <MapPin className="inline w-3 h-3 -mt-0.5 mr-1" />
                    {offer.distance_km} km udaljenosti · GPS {offer.gps_freshness}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Odredište</p>
                  <p className="text-sm text-white truncate" data-testid="offer-dropoff">
                    {booking?.dropoff_location || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4 grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Putnici</p>
              <p className="text-sm text-white">{booking?.passengers || "-"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500">Vrijeme</p>
              <p className="text-sm text-white tabular-nums">
                {booking?.pickup_date} · {booking?.pickup_time}
              </p>
            </div>
            {booking?.flight_number ? (
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Let</p>
                <p className="text-sm text-white">{booking.flight_number}</p>
              </div>
            ) : null}
            {booking?.quote_amount ? (
              <div className="col-span-2">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Cijena</p>
                <p className="font-display text-2xl text-[#C5A059] tabular-nums">
                  €{Number(booking.quote_amount).toFixed(2)}
                </p>
              </div>
            ) : null}
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4 flex items-center gap-3">
            <User className="w-4 h-4 text-zinc-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white">{booking?.name || "-"}</p>
              <p className="text-[11px] text-zinc-500 flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {booking?.phone || "-"}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        {isPending && (
          <div className="grid grid-cols-2 gap-3 mt-6">
            <button data-testid="offer-decline-btn" disabled={busy} onClick={decline}
                    className="h-14 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm uppercase tracking-widest disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" /> Odbij
            </button>
            <button data-testid="offer-accept-btn" disabled={busy} onClick={accept}
                    className="h-14 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold text-sm uppercase tracking-widest disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" /> Prihvati
            </button>
          </div>
        )}
        {isExpired && (
          <button onClick={() => navigate("/driver/dashboard")}
                  className="mt-6 w-full h-12 rounded-full border border-white/20 text-xs uppercase tracking-widest">
            <Navigation className="inline w-3 h-3 mr-2" />
            Natrag na početnu
          </button>
        )}
      </div>
    </div>
  );
}
